import {Injectable} from '@angular/core';
import {User} from '../model/user.model';
import {UserService} from '../service/user.service';
import {Keys, StorageService} from '../service/storage.service';
import {Proof} from '../model/Proof';
import {LoanApplicationDocumentType} from '../model/loan-application-document-type.model';
import {ContactInfos} from '../model/contact-infos.model';
import {StringUtil} from '../utils/string.util';
import {EmailAddresses} from '../model/email-addresses.model';

@Injectable()
export class UserInteractor {
    private readonly userService: UserService;
    private readonly storageService: StorageService;
    private readonly stringUtil: StringUtil;

    constructor(storageService: StorageService, userService: UserService, stringUtil: StringUtil) {
        this.storageService = storageService;
        this.userService = userService;
        this.stringUtil = stringUtil;
    }

    public async getUserId(): Promise<string> {
        return this.storageService.getItem(Keys.PROFILE_ID);
    }

    public async getEmail(): Promise<string> {
        const userId = await this.getUserId();
        const user = await this.userService.getProfileDetails(userId);
        return user.email;
    }

    public async update(user: User): Promise<void> {
        await this.userService.update(user);
    }

    public async get(): Promise<User> {
        const userId = await this.getUserId();
        return await this.userService.getProfileDetails(userId);
    }

    public async addPanDocument(fileIds: string[]): Promise<void> {
        const userId = await this.getUserId();
        const user = await this.userService.getProfileDetails(userId);
        let proof: Proof;
        if (user.pan && user.pan.proofs && user.pan.proofs.length > 0) {
            proof = user.pan.proofs[0];
        } else {
            proof = new Proof(LoanApplicationDocumentType.PAN);
        }
        proof.fileIds = proof.fileIds ? proof.fileIds : [];
        proof.fileIds.push(...fileIds);
        user.pan.proofs = [proof];
        await this.userService.update(user);
    }

    public async addProfilePhoto(fileIds: string[]): Promise<void> {
        const userId = await this.getUserId();
        const user = await this.userService.getProfileDetails(userId);
        if (user.identity && user.identity.proofs && user.identity.proofs.length > 0) {
            user.identity.proofs[0].fileIds.push(...fileIds);
        } else {
            const proof = new Proof(LoanApplicationDocumentType.PHOTO);
            proof.fileIds = fileIds;
            user.identity = {proofs: [proof]};
        }
        await this.userService.update(user);
    }

    public async removePhotoFile(fileId: string): Promise<void> {
        const userId = await this.getUserId();
        const user = await this.userService.getProfileDetails(userId);
        if (user.identity && user.identity.proofs && user.identity.proofs.length > 0 && user.identity.proofs[0].fileIds) {
            user.identity.proofs[0].fileIds.splice(user.identity.proofs[0].fileIds.indexOf(fileId), 1);
        } else {
            user.identity.proofs = [];
        }
        await this.userService.update(user);
    }

    public async removePanFile(fileId: string): Promise<void> {
        const userId = await this.getUserId();
        const user = await this.userService.getProfileDetails(userId);
        if (user.pan && user.pan.proofs && user.pan.proofs.length > 0 && user.pan.proofs[0].fileIds) {
            user.pan.proofs[0].fileIds.splice(user.pan.proofs[0].fileIds.indexOf(fileId), 1);
        } else {
            user.pan.proofs = [];
        }
        await this.userService.update(user);
    }

    async updateAadhaarLinkToMobileAnswer(aadhaarLinkedWithMobileAnswer: boolean) {
        const userId = await this.getUserId();
        await this.userService.captureAadhaarLinkedWithMobileAnswer(userId, aadhaarLinkedWithMobileAnswer);
    }

    async getUsersByPhoneNumber(phoneNumber: string): Promise<User[]> {
        return await this.userService.getUsersByPhoneNumber(phoneNumber);
    }

    async getUserByPan(pan: string): Promise<User> {
        return await this.userService.getUserByPan(pan);
    }

    // TODO: mask phone-number from server
    async getContactInfosByUserId(userId: string): Promise<ContactInfos> {
        const contactInfos = await this.userService.getContactInfosByUserId(userId);
        for (const contact of contactInfos) {
            contact.maskedPhoneNumber = this.stringUtil.maskPhoneNumber(contact.phoneNumber);
        }
        return contactInfos;
    }

    async updatePhoneNumberAndMarkOtpAsVerified(userId: string, phoneNumber: string) {
        await this.userService.updatePhoneNumberAndMarkOtpAsVerified(userId, phoneNumber);
    }

    async markContactAsPrimary(userId: string, phoneNumber: string) {
        return await this.userService.markContactAsPrimary(userId, phoneNumber);
    }

    async getEmailAddressesByUserId(userId: string): Promise<EmailAddresses> {
        return await this.userService.getEmailAddressesByUserId(userId);
    }

    async markEmailAddressAsPrimary(userId: string, emailId: string) {
        return await this.userService.markEmailAddressAsPrimary(userId, emailId);
    }
}
